/**
 * O que conta como "ficheiro de modelo" para os selectores de ficheiro,
 * o arrastar-e-largar e o carregador.
 *
 * Existe porque a lista estava escrita à mão em oito sítios e porque
 * abrir um `.frag` NÃO é o mesmo que abrir um IFC: um Fragments nativo é
 * geometria já derivada, sem o STEP por trás. Tudo o que lê o IFC bruto
 * (o IDS, o BoQ, a Tabela, o IFCEdit, o assistente, a escrita de
 * georreferenciação) não tem por onde responder e responderia "zero",
 * que é pior do que não responder. Enquanto esses consumidores não
 * souberem dizer "este modelo não traz IFC", abrir `.frag` fica atrás da
 * flag `openFragments`, desligada.
 *
 * Guardar `.frag` e reutilizar a derivação do próprio IFC em cache não
 * têm esse problema: em ambos os casos o IFC continua lá.
 */
import { isFeatureEnabled } from '@core/features'
import { POINT_CLOUD_EXTENSIONS } from '@viewer/point-cloud/pointCloudApi'

export const FRAGMENT_EXTENSIONS = ['.frag', '.fragments'] as const
export const IFC_EXTENSIONS = ['.ifc', '.ifczip'] as const

/** IFC5. Entra no mesmo selector porque é isso que a pessoa espera ao
 *  abrir um modelo, mas o carregador desvia-o para o painel do IFC5:
 *  não é STEP, não passa pelo web-ifc e não vira Fragments. */
export const IFCX_EXTENSIONS = ['.ifcx'] as const

/** Um `.frag` nativo pode ser aberto como modelo nesta instalação? */
export function canOpenFragments(): boolean {
  return isFeatureEnabled('openFragments')
}

/** As nuvens de pontos abrem-se pelo mesmo "Abrir modelo", quando a
 *  funcionalidade está ligada. Quem quer abrir só um levantamento não
 *  tem de saber que existe um painel à parte para isso: escolhe o
 *  ficheiro, e o carregador desvia-o para o sítio certo, como faz com o
 *  IFC5. A lista é a do leitor de nuvens, e não uma cópia dela. */
export function canOpenPointClouds(): boolean {
  return isFeatureEnabled('pointClouds')
}

export function isPointCloudFilename(name: string): boolean {
  const n = name.toLowerCase()
  return POINT_CLOUD_EXTENSIONS.some((ext) => n.endsWith(ext))
}

/** Valor para o atributo `accept` de um `<input type="file">`. */
export function modelAcceptAttr(): string {
  const base: string[] = [...IFC_EXTENSIONS, ...IFCX_EXTENSIONS]
  if (canOpenPointClouds()) base.push(...POINT_CLOUD_EXTENSIONS)
  return canOpenFragments()
    ? [...base, ...FRAGMENT_EXTENSIONS].join(',')
    : base.join(',')
}

/** Extensões aceites pelo selector do File System Access (Chrome, Edge).
 *
 *  A MESMA lista do `<input>` clássico. Era uma lista à parte, só com
 *  `.ifc` e `.frag`, e o filtro por omissão do Chrome escondia o `.ifcx`,
 *  as nuvens de pontos e o `.ifczip`, que o carregador abre: parecia que
 *  não eram suportados. */
export function modelPickerExtensions(): string[] {
  return modelAcceptAttr().split(',')
}

/** O nome do filtro no selector do sistema. */
export function modelPickerDescription(): string {
  return 'Modelos suportados'
}

/** O nome é de um Fragments nativo (independentemente da flag). */
export function isFragmentFilename(name: string): boolean {
  return /\.(frag|fragments)$/i.test(name)
}

/** O nome é de um IFC5. */
export function isIfcxFilename(name: string): boolean {
  return /\.ifcx$/i.test(name)
}

/** O ficheiro pode ser largado na área do modelo.
 *
 *  O `.ifcx` entra: quem o arrasta está a pedir para o ver, e o
 *  carregador desvia-o para o painel do IFC5. Recusá-lo aqui dava um
 *  "formato não suportado" que só seria teimosia nossa. */
export function isDroppableModelFile(name: string): boolean {
  if (isFragmentFilename(name)) return canOpenFragments()
  if (isPointCloudFilename(name)) return canOpenPointClouds()
  return /\.(ifc|ifczip|ifcx)$/i.test(name)
}
